import { Component, ElementRef, EventEmitter, Input, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Clip } from '@modules/preview/types/clip';
import { StreamStatus } from '@modules/preview/types/stream-status';
import { Timecode } from '@modules/preview/types/timecode';

@Component({
    selector: 'app-preview-timecode',
    templateUrl: './preview-timecode.component.html',
    styleUrl: './preview-timecode.component.less',
    standalone: false
})
export class PreviewTimecodeComponent {

  // ViewChild
  @ViewChild('timecode', { static: false }) timecodeRef?: ElementRef;
  @ViewChild('timecodeInputElement', { static: false }) timecodeInputRef?: ElementRef;

  // Input
  @Input() status?: StreamStatus;
  @Input() clip?: Clip;
  @Input() record = false;

  // Output
  @Output() jump = new EventEmitter<{tc?: string, frame?: number, offset?: number}>();

  // Public
  public timecode?: string;  // Timecode string expected as HH:MM:SS:FF or HH:MM:SS;FF or HH:MM:SS.SSSS
  public timecodeInput?: string;
  public placeholder = '00:00:00:00';
  public mask = '00:00:00:00';
  public canEdit = false;
  public editing = false;
  public error?: Error;

  // Private
  private tcRegex = /^(\d\d):(\d\d):(\d\d)(:|;|\.)(\d{2,4})$/;
  private irig = false;

  /**
   * Constructor
   */

  constructor() { }

  /**
   * Component lifecycle
   */

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('clip' in changes) {
      this.updatePlaceholderAndMask();
    }
    this.canEdit = !!this.status?.playback?.isLoaded && !!this.clip;
    if (!this.canEdit) {
      this.endEdit();
    }
  }

  /**
   * Methods
   */

  startEdit(): void {
    if (!this.canEdit) {
      return;
    }
    if (!this.editing) {
      this.timecodeInput = this.timecode;
    }
    this.editing = true;
    setTimeout(() => this.timecodeInputRef?.nativeElement?.focus(), 50);
  }

  endEdit(): void {
    this.editing = false;
    setTimeout(() => {
      if (this.timecodeInputRef && this.timecodeRef) {
        this.timecodeRef.nativeElement.innerHTML = this.timecodeInputRef || '00:00:00:00'
      }
    }, 10);
  }

  jumpTC(timecode: string): void {
    if (!timecode?.length || this.error) {
      return;
    }
    const tc = new Timecode(timecode, this.clip?.timebase ?? 24, !!this.clip?.drop);
    let frame = tc.frameCount - (this.clip?.startTimecodeFrames ?? 0);
    frame = Math.min(Math.max(frame, 0), ((this.clip?.durationFrames ?? 0) - 1));
    this.jump.emit({ frame });
  }

  setValue(timecode: string): void {
    this.timecode = timecode;
    if (this.timecodeRef) {
      this.timecodeRef.nativeElement.innerHTML = this.timecode || '00:00:00:00';
    }
  }

  validate(value: string): boolean {
    const result = this.tcRegex.exec(value);
    if (result === null) {
      this.error = new Error('Invalid Timecode');
      this.error.name = 'invalid';
      return false;
    }
    if (result) {
      try {
        const timecode = new Timecode(value, this.clip?.timebase ?? 24, !!this.clip?.drop);
      } catch (error) {
        this.error = (error as Error);
        this.error.name = 'cant_parse';
        return false;
      }
    }
    this.error = undefined;
    return true;
  }

  updatePlaceholderAndMask(): void {
    if (this.irig) {
      this.placeholder = '00:00:00.0000';
    } else if ((this.clip?.drop ?? 0) > 0) {
      this.placeholder = '00:00:00;00';
    } else {
      this.placeholder = '00:00:00:00';
    }
    this.mask = this.placeholder;
  }

  nextFrame(): void {
    if (!this.timecodeInput || !this.validate(this.timecodeInput)) { return; }
    let timecode = new Timecode(this.timecodeInput, this.clip?.timebase ?? 24, !!this.clip?.drop);
    timecode = timecode.add(1);
    this.timecodeInput = timecode.toString();
  }
  
  prevFrame(): void {
    if (!this.timecodeInput || !this.validate(this.timecodeInput)) { return; }
    let timecode = new Timecode(this.timecodeInput, this.clip?.timebase ?? 24, !!this.clip?.drop);
    timecode = timecode.add(-1);
    this.timecodeInput = timecode.toString();
  }

  /**
   * Actions
   */

  save(): void {
    if (this.timecodeInput) {
      this.jumpTC(this.timecodeInput);
    }
    this.close();
  }

  close(): void {
    this.endEdit();
  }

}
